import * as THREE from 'three';
import { stdMat } from '../render/scene';
/* ============================================================
   敵人造型的共用技法與色盤（移植自 `enemy_sample_ani.html` §1–§4）
   ============================================================

   權威規格是 `05-ENEMY.md`。這個檔只做兩件事：**幾何池**與**色盤**，
   加上三個沒有階層時也能做動畫的技法（`pt` / `pivotTop` / `legSwing`）。

   ## 為什麼幾何要另外開一份（不共用 `render/scene.ts` 的 G）

   小怪走 `InstancedMesh` + `instanceColor` 做受擊閃白（材質是型別共用的，
   改材質會讓整個型別一起閃）。而 `instanceColor` 要生效**必須開
   `vertexColors`**；一旦開了，幾何沒有 `color` 屬性時 WebGL 餵的預設值是
   `(0,0,0)` —— **整台變全黑**。所以幾何池建立時就補一份全 1（`withColor`）。
   規格 §6.5 特別標了這個地雷，因為它的症狀（全黑）與原因（缺屬性）離很遠。

   ## 色彩處理（使用者 2026-08-02：「新版的敵人和 boss 在色彩上略有不足」）

   規格的色盤是刻意樸素的：一整排 `0x59616f` 冷灰機體，顏色只出現在
   發光件上。那在圖鑑裡讀得清楚，但**放進戰場會少一層**——
   場地本身就是冷藍，於是 28 種敵人在遠處讀起來是同一團灰。

   處理方式是**把威脅色染進裝甲本身，但只動色相與飽和度、不動明度**：

     armor = HSL( 威脅色的色相, 低飽和, 原本的明度 )

   這樣三條分辨規則一條都沒破 —— 明度仍然是玩家的 1/2.2（規則 1）、
   仍然偏冷（規則 2）、仍然沒有綠（規則 3）—— 但**遠看整隻有色調傾向**，
   四色語彙從「一個發光點」變成「一整隻」。

   三段強度做成可切換（`ENEMY_PAL`），因為這是品味判斷，要並排看才選得出來：
     spec  完全照規格（飽和 0）
     tint  低飽和染色（預設）
     duo   高飽和 + 明度補償，走「有色裝甲」路線

   冷色（紫／天藍）另外加明度補償：場地是冷藍，同明度的冷色會融進背景，
   而暖色不會 —— 這是「氛圍不能吃掉可讀性」的老規矩。 */

function withColor(g) {
  var n = g.attributes.position.count, a = new Float32Array(n * 3);
  for (var i = 0; i < n * 3; i++) a[i] = 1;
  g.setAttribute('color', new THREE.BufferAttribute(a, 3));
  return g;
}

/** 敵人專用的幾何池。單位尺寸：scale 直接等於實際尺寸。
    `tor` 的 x 讀作外徑（(0.40+0.10)×2 = 1.0），z 讀作管厚倍率。 */
export var EG = {
  box: withColor(new THREE.BoxGeometry(1, 1, 1)),
  cyl: withColor(new THREE.CylinderGeometry(0.5, 0.5, 1, 10)),
  bev: withColor(new THREE.CylinderGeometry(0.5, 0.5, 1, 8, 1, false, Math.PI / 8)),
  cone: withColor(new THREE.ConeGeometry(0.5, 1, 8)),
  oct: withColor(new THREE.OctahedronGeometry(0.5)),
  tor: withColor(new THREE.TorusGeometry(0.40, 0.10, 8, 18))
};

/** 四色威脅語彙（規格 §2.3）。顏色講「牠怎麼傷你」，不講「牠是誰」。 */
export var TINT = {
  red: { hex: 0xff4a2c, zh: '接觸', tip: '碰到你才有傷害' },
  amb: { hex: 0xffc23d, zh: '直射', tip: '直線彈道，會被擋' },
  vio: { hex: 0xc46bff, zh: '範圍', tip: '地面預警圈，走開就好' },
  azu: { hex: 0x4f9dff, zh: '支援', tip: '自己不傷你，讓別人變難打' }
};

/** 玩家專用色。**整個遊戲裡綠色只會出現在你身上**（規格 §3 規則 3）。 */
export var PLR_GREEN = 0x74ff9c;

/* 規格的基準色（染色前）。 */
var BASE = { armor: 0x59616f, armorE: 0x8d99b0, dark: 0x161c27, trim: 0x090c13 };

/** 色彩處理的強度。`sat` 是染進裝甲的飽和度，`lift` 是明度倍率。 */
var MODES = {
  spec: { armor: 0.00, dark: 0.00, trim: 0.00, lift: 1.00, ei: 1.00 },
  tint: { armor: 0.17, dark: 0.11, trim: 0.06, lift: 1.03, ei: 1.05 },
  duo: { armor: 0.32, dark: 0.18, trim: 0.09, lift: 1.13, ei: 1.18 }
};
/** 冷色在冷藍場地上要多一點明度才浮得出來；暖色不用。 */
var COOL_LIFT = { red: 1.00, amb: 1.00, vio: 1.07, azu: 1.10 };

export var ENEMY_PAL = { mode: 'tint' };

var _c = new THREE.Color(), _t = new THREE.Color(), _h = { h: 0, s: 0, l: 0 }, _th = { h: 0, s: 0, l: 0 };
/** 把 `base` 染成「威脅色的色相 + 指定飽和度」，**明度維持不變**。
    直接做 RGB 混色會把明度一起拉高，那會破壞規則 1（玩家亮 2.2 倍）。 */
function tintTo(base, threat, sat, lift) {
  _c.setHex(base); _t.setHex(threat);
  _c.getHSL(_h); _t.getHSL(_th);
  if (sat <= 0) return base;
  _c.setHSL(_th.h, sat, Math.min(0.92, _h.l * lift));
  return _c.getHex();
}

var CACHE = {};
/** 一種威脅色的一整組材質。同一組會被該色的所有型別共用 ——
    `InstancedMesh` 是按「型別 × 部件」開的，材質共用不影響 draw call，
    但**材質共用代表受擊回饋不能改材質**（要用 instanceColor，見檔頭）。 */
export function enemyPalette(tint) {
  var key = tint + ':' + ENEMY_PAL.mode;
  if (CACHE[key]) return CACHE[key];
  var M = MODES[ENEMY_PAL.mode] || MODES.tint;
  var hex = (TINT[tint] || TINT.red).hex;
  var lift = M.lift * (COOL_LIFT[tint] || 1);
  var p = {
    armor: stdMat(tintTo(BASE.armor, hex, M.armor, lift), { metal: 0.52, rough: 0.46, flat: true, vcol: true }),
    armorE: stdMat(tintTo(BASE.armorE, hex, M.armor * 0.8, lift), { metal: 0.56, rough: 0.40, flat: true, vcol: true }),
    dark: stdMat(tintTo(BASE.dark, hex, M.dark, lift), { metal: 0.58, rough: 0.55, flat: true, vcol: true }),
    trim: stdMat(tintTo(BASE.trim, hex, M.trim, lift), { metal: 0.40, rough: 0.64, flat: true, vcol: true }),
    glow: stdMat(0x090b10, { em: hex, ei: 1.75 * M.ei, flat: true, vcol: true }),
    glowE: stdMat(0x090b10, { em: hex, ei: 2.9 * M.ei, flat: true, vcol: true }),
    /* 深層小怪唯一的新材質（規格 §2.2b）。`MeshBasicMaterial` **完全不受光** ——
       不管打光怎麼變它都是同一片死黑，在一個所有東西都靠平面著色分面的世界裡
       讀作「這裡被挖掉了」，而不是「這裡有個黑色零件」。

       它**不是第五個威脅色**：四色語彙講「牠怎麼傷你」，虛空講「這東西不對」，
       兩者是不同的軸。所以它刻意不吃 `tint`，四種威脅色共用同一片黑。

       `vertexColors` 要開 —— 不開就吃不到 `instanceColor` 的受擊白閃。
       基色夠暗，閃起來只是從死黑變成深藍灰，讀作「一個會眨的洞」。 */
    void: new THREE.MeshBasicMaterial({ color: 0x04050a, vertexColors: true }),
    hex: hex
  };
  CACHE[key] = p;
  return p;
}
/** 換色彩模式（並行比較用）。會清掉材質快取，下一次建型別時重新產生。 */
export function setEnemyPalette(mode) {
  if (!MODES[mode]) return null;
  ENEMY_PAL.mode = mode; CACHE = {};
  return mode;
}
export function enemyPaletteModes() { return Object.keys(MODES); }

/* ---------- 技法（規格 §3 / §6.1） ---------- */

/** 一個部件。`fn(P, o)` 只負責「相對本體會動的」——
    沒有 `fn` 的部件在精英 rig 裡才合併得掉，所以少寫一個 fn 就少一個 draw call。 */
export function pt(g, m, s, p?, r?, fn?) {
  return { g: g, m: m, s: s, p: p || [0, 0, 0], r: r || null, fn: fn || null };
}

/** 沒有階層時怎麼做關節：繞「上端」轉而不是繞自身中心。
    `InstancedMesh` 的每個 primitive 是獨立實體、沒有 parent，
    所以樞紐只能用「轉完再補一段位移」湊出來。`half` = 部件長度的一半。

    **限制：只能單節。** 兩節以上補償會互相污染 ——
    要膝蓋就升級成精英或 Boss（規格 §6.1）。 */
export function pivotTop(P, a, half) {
  P.rx += a;
  P.y += half * (1 - Math.cos(a));
  P.z -= half * Math.sin(a);
}

/** 兩腿交替擺動，繞髖（部件上端）轉。 */
export function legSwing(side, half, amp) {
  return function (P, o) {
    var a = Math.sin((o.ph + (side > 0 ? 0 : 0.5)) * Math.PI * 2) * amp * (0.22 + 0.78 * o.mv) * o.q.lg;
    pivotTop(P, a, half);
  };
}

export var HP = Math.PI / 2;

/* ---------- 刻意與原型不同的一項：精英的加成部件 ----------

   原型的精英是 `T.parts + T.ex + eliteKit(T)`（`enemy_sample_ani.html:3488`），
   `eliteKit()` 是**脈動冠環 + 頂珠 + 天線 + 兩片肩甲**共 5 個部件（1556-1571）。

   **我們只做冠環，而且是全場共用的一顆簡化 torus**（`render/enemy-field.ts`）——
   使用者 2026-08-03 看過兩版之後拍板「保留現狀就好，天線不必要」。
   代價寫在這裡而不是靠記憶：戰場鏡頭下多出來的四個部件在 20px 只是噪音，
   而共用那一顆讓全場的精英只花 1 個 draw call。

   **這是唯一一項刻意不 1:1 的造型落差**，不要「順手補回來」。 */
